import { FormattedMessage } from "react-intl";

import { Button } from "@ps-design-system/components/Button";
import { Divider } from "@ps-design-system/components/Divider";
import { Form } from "@ps-design-system/components/Form";
import { Text } from "@ps-design-system/components/Text";

import type { Space } from "../../../../../types/spaces.types";
import { trackFormNavClicked } from "../../utils/your-space.utils";
import { useSpaceRulesForm } from "../useSpaceRulesForm";

import { GeneralRulesField } from "./form-fields/GeneralRulesField";
import { MinAgeField } from "./form-fields/MinAgeField";

type SpaceRulesFormProps = {
  space: Space;
};

/**
 * The form component for editing space rules, including house rules and
 * minimum age. It uses the `useSpaceRulesForm` hook to manage form state and
 * handle submission.
 */
const SpaceRulesForm = ({ space }: SpaceRulesFormProps) => {
  const { form, onSave, errorMsg } = useSpaceRulesForm(space);

  const handleSave = async () => {
    const success = await onSave();
    if (success) {
      trackFormNavClicked("next");
      window.location.href =
        space.status === "DRAFT"
          ? `/your-space/${space._id}/arrival-guide`
          : `/your-space/${space._id}`;
    }
  };

  const handleBack = () => trackFormNavClicked("previous");

  return (
    <Form {...form} spacing={0}>
      <GeneralRulesField />

      <Divider spacing={48} />

      <MinAgeField />

      <Divider spacing={48} />

      <div className="tw-flex tw-justify-between tw-mt-32 tw-items-start">
        <Button
          appearance="white"
          variant="outline-1"
          href={`/your-space/${space._id}`}
          onClick={handleBack}
        >
          <FormattedMessage
            id="hosting.space_rules.back_button"
            defaultMessage="Back"
          />
        </Button>

        <div className="tw-flex tw-flex-col tw-items-end tw-gap-8">
          <Button
            appearance="purple"
            disabled={!form.formState.isDirty}
            onClick={handleSave}
            showSpinner={form.formState.isSubmitting}
            testId="space-rules-save-button"
          >
            <FormattedMessage
              id="hosting.space_rules.save_button"
              defaultMessage="Save"
            />
          </Button>
          {errorMsg && (
            <Text
              typeface="body-1"
              element="p"
              className="tw-text-red-800"
              testId="space-rules-submit-error"
            >
              {errorMsg}
            </Text>
          )}
        </div>
      </div>
    </Form>
  );
};

export { SpaceRulesForm };
